/**
 * Production configuration and best practices
 * 
 * Configures environment-specific settings for development vs production builds.
 * Handles performance monitoring, security settings, API configuration, and feature flags.
 */

// Environment detection
export const IS_PRODUCTION = import.meta.env.PROD;
export const IS_DEVELOPMENT = import.meta.env.DEV;

// Performance monitoring (production-safe but limited)
export const ENABLE_PERFORMANCE_MONITORING = true; // Always enabled for health monitoring
export const ENABLE_DIAGNOSTICS = IS_DEVELOPMENT; // Keep dev-only
export const ENABLE_DEBUG_LOGGING = IS_DEVELOPMENT;

// Security settings
export const SECURITY_CONFIG = {
  // Enable strict CSP in production
  enableCSP: IS_PRODUCTION,
  
  // Remove debug info from error messages in production
  hideErrorDetails: IS_PRODUCTION,
  
  // Disable dev tools in production
  disableDevTools: IS_PRODUCTION,
  
  // Enable HTTPS-only cookies in production
  secureOnly: IS_PRODUCTION
};

// Bundle optimization
export const BUNDLE_CONFIG = {
  // Lazy load non-critical components
  enableLazyLoading: true,
  
  // Tree-shake unused code
  enableTreeShaking: IS_PRODUCTION,
  
  // Minify in production
  enableMinification: IS_PRODUCTION,
  
  // Source maps only in development
  enableSourceMaps: IS_DEVELOPMENT
};

// API configuration
export const API_CONFIG = {
  // Use empty string for production (same-origin), proper localhost for development
  baseUrl: IS_PRODUCTION ? '' : 'http://localhost:3000',
  
  // Request timeout (production-safe)
  timeout: IS_PRODUCTION ? 15000 : 30000,
  
  // Retry configuration (more retries in production for reliability)
  maxRetries: IS_PRODUCTION ? 5 : 1,
  
  // Production-safe fallbacks - use function to avoid module-level window access
  get fallbackUrl() {
    return IS_PRODUCTION ? (typeof window !== 'undefined' ? window.location.origin : '') : 'http://localhost:3000';
  }
};

// Feature flags for production deployment
export const FEATURE_FLAGS = {
  // Debug features (development only)
  showPerformanceHUD: IS_DEVELOPMENT,
  showDiagnosticOverlay: IS_DEVELOPMENT,
  
  // Progressive enhancement
  enableServiceWorker: false, // Disabled to fix deployment issues
  enablePushNotifications: IS_PRODUCTION,
  
  // Analytics (production only)
  enableAnalytics: IS_PRODUCTION,
  
  // Error reporting (production only)  
  enableErrorReporting: IS_PRODUCTION
} as const;

/**
 * Get production logging configuration
 * Returns settings for safe logging without overriding console methods
 */
export const getLoggingConfig = () => ({
  enableDebug: IS_DEVELOPMENT && ENABLE_DEBUG_LOGGING,
  enableInfo: IS_DEVELOPMENT,
  enableWarn: true,
  enableError: true,
  sanitizeOutput: IS_PRODUCTION
});

// Export type for type safety
export type FeatureFlag = keyof typeof FEATURE_FLAGS;